/**
 * Image optimization utilities for mobile devices
 * Handles compression, resizing, enhancement, and memory-safe processing
 * Optimized for trading cards and sneakers recognition
 */

// Configuration for image optimization
const IMAGE_CONFIG = {
  maxWidth: 1024,
  maxHeight: 1024,
  quality: 0.85,
  maxFileSizeMB: 2,
  mimeType: 'image/jpeg'
};

/**
 * Apply image enhancements for better recognition
 * - Auto-contrast
 * - Sharpening
 * - Color normalization
 */
const enhanceImage = (ctx, width, height) => {
  const imageData = ctx.getImageData(0, 0, width, height);
  const data = imageData.data;
  
  // Calculate histogram for auto-levels
  let minR = 255, maxR = 0;
  let minG = 255, maxG = 0;
  let minB = 255, maxB = 0;
  
  for (let i = 0; i < data.length; i += 4) {
    minR = Math.min(minR, data[i]);
    maxR = Math.max(maxR, data[i]);
    minG = Math.min(minG, data[i + 1]);
    maxG = Math.max(maxG, data[i + 1]);
    minB = Math.min(minB, data[i + 2]);
    maxB = Math.max(maxB, data[i + 2]);
  }
  
  // Apply auto-levels (stretch contrast)
  const rangeR = maxR - minR || 1;
  const rangeG = maxG - minG || 1;
  const rangeB = maxB - minB || 1;
  
  for (let i = 0; i < data.length; i += 4) {
    data[i] = Math.round(((data[i] - minR) / rangeR) * 255);
    data[i + 1] = Math.round(((data[i + 1] - minG) / rangeG) * 255);
    data[i + 2] = Math.round(((data[i + 2] - minB) / rangeB) * 255);
  }
  
  ctx.putImageData(imageData, 0, 0);
  
  // Apply subtle sharpening using convolution
  applySharpening(ctx, width, height, 0.3);
};

/**
 * Apply sharpening filter
 */
const applySharpening = (ctx, width, height, amount = 0.3) => {
  const imageData = ctx.getImageData(0, 0, width, height);
  const data = imageData.data;
  const copy = new Uint8ClampedArray(data);
  
  // Sharpening kernel
  const kernel = [
    0, -amount, 0,
    -amount, 1 + 4 * amount, -amount,
    0, -amount, 0
  ];
  
  for (let y = 1; y < height - 1; y++) {
    for (let x = 1; x < width - 1; x++) {
      for (let c = 0; c < 3; c++) {
        let sum = 0;
        for (let ky = -1; ky <= 1; ky++) {
          for (let kx = -1; kx <= 1; kx++) {
            const idx = ((y + ky) * width + (x + kx)) * 4 + c;
            sum += copy[idx] * kernel[(ky + 1) * 3 + (kx + 1)];
          }
        }
        data[(y * width + x) * 4 + c] = Math.max(0, Math.min(255, sum));
      }
    }
  }
  
  ctx.putImageData(imageData, 0, 0);
};

/**
 * Detect and crop to the main item in the image
 * Uses edge detection to find the primary object
 */
const autoCropToItem = (ctx, width, height) => {
  const imageData = ctx.getImageData(0, 0, width, height);
  const data = imageData.data;
  
  // Convert to grayscale and detect edges
  const gray = new Uint8Array(width * height);
  for (let i = 0; i < data.length; i += 4) {
    gray[i / 4] = Math.round(0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2]);
  }
  
  // Find bounding box of non-background pixels
  // Use edge detection threshold
  const threshold = 30;
  let minX = width, maxX = 0, minY = height, maxY = 0;
  let hasContent = false;
  
  // Simple background detection (assumes corners are background)
  const bgSamples = [
    gray[0], gray[width - 1], 
    gray[(height - 1) * width], gray[height * width - 1]
  ];
  const avgBg = bgSamples.reduce((a, b) => a + b, 0) / 4;
  
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const idx = y * width + x;
      if (Math.abs(gray[idx] - avgBg) > threshold) {
        hasContent = true;
        minX = Math.min(minX, x);
        maxX = Math.max(maxX, x);
        minY = Math.min(minY, y);
        maxY = Math.max(maxY, y);
      }
    }
  }
  
  if (!hasContent || maxX - minX < 50 || maxY - minY < 50) {
    // No significant crop needed or image too small
    return { x: 0, y: 0, width, height, cropped: false };
  }
  
  // Add padding (10%)
  const padX = Math.round((maxX - minX) * 0.1);
  const padY = Math.round((maxY - minY) * 0.1);
  
  return {
    x: Math.max(0, minX - padX),
    y: Math.max(0, minY - padY),
    width: Math.min(width, maxX - minX + 2 * padX),
    height: Math.min(height, maxY - minY + 2 * padY),
    cropped: true
  };
};

/**
 * Compress and resize an image to prevent memory issues
 * @param {string} dataUrl - Base64 data URL of the image
 * @param {object} options - Compression options
 * @returns {Promise<string>} - Compressed base64 data URL
 */
export const compressImage = (dataUrl, options = {}) => {
  const config = { ...IMAGE_CONFIG, ...options };
  
  return new Promise((resolve, reject) => {
    const img = new Image();
    
    img.onload = () => {
      try {
        // Calculate new dimensions while maintaining aspect ratio
        let { width, height } = img;
        
        if (width > config.maxWidth || height > config.maxHeight) {
          const ratio = Math.min(config.maxWidth / width, config.maxHeight / height);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }
        
        // Create canvas for compression
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        
        const ctx = canvas.getContext('2d');
        
        // Use better image smoothing for quality
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        
        // Draw image
        ctx.drawImage(img, 0, 0, width, height);
        
        // Convert to compressed JPEG
        const compressedDataUrl = canvas.toDataURL(config.mimeType, config.quality);
        
        // Clean up
        canvas.width = 0;
        canvas.height = 0;
        
        resolve(compressedDataUrl);
      } catch (error) {
        reject(error);
      }
    };
    
    img.onerror = () => {
      reject(new Error('Failed to load image for compression'));
    };
    
    img.src = dataUrl;
  });
};

/**
 * Enhanced image processing for better AI recognition
 * Applies auto-crop, contrast enhancement, and sharpening
 * @param {string} dataUrl - Base64 data URL of the image
 * @param {object} options - Processing options
 * @returns {Promise<string>} - Enhanced base64 data URL
 */
export const enhanceForRecognition = (dataUrl, options = {}) => {
  const config = { 
    ...IMAGE_CONFIG, 
    enhance: true,
    autoCrop: true,
    ...options 
  };
  
  return new Promise((resolve, reject) => {
    const img = new Image();
    
    img.onload = () => {
      try {
        let { width, height } = img;
        
        // First pass: draw original for analysis
        const analysisCanvas = document.createElement('canvas');
        analysisCanvas.width = width;
        analysisCanvas.height = height;
        const analysisCtx = analysisCanvas.getContext('2d');
        analysisCtx.drawImage(img, 0, 0);
        
        // Auto-crop detection
        let cropInfo = { x: 0, y: 0, width, height, cropped: false };
        if (config.autoCrop) {
          cropInfo = autoCropToItem(analysisCtx, width, height);
        }
        
        // Clean up analysis canvas
        analysisCanvas.width = 0;
        analysisCanvas.height = 0;
        
        // Calculate final dimensions
        let finalWidth = cropInfo.width;
        let finalHeight = cropInfo.height;
        
        if (finalWidth > config.maxWidth || finalHeight > config.maxHeight) {
          const ratio = Math.min(config.maxWidth / finalWidth, config.maxHeight / finalHeight);
          finalWidth = Math.round(finalWidth * ratio);
          finalHeight = Math.round(finalHeight * ratio);
        }
        
        // Create final canvas
        const canvas = document.createElement('canvas');
        canvas.width = finalWidth;
        canvas.height = finalHeight;
        const ctx = canvas.getContext('2d');
        
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        
        // Draw cropped region
        ctx.drawImage(
          img, 
          cropInfo.x, cropInfo.y, cropInfo.width, cropInfo.height,
          0, 0, finalWidth, finalHeight
        );
        
        // Apply enhancements
        if (config.enhance) {
          enhanceImage(ctx, finalWidth, finalHeight);
        }
        
        // Convert to JPEG
        const enhancedDataUrl = canvas.toDataURL(config.mimeType, config.quality);
        
        // Clean up
        canvas.width = 0;
        canvas.height = 0;
        
        resolve(enhancedDataUrl);
      } catch (error) {
        reject(error);
      }
    };
    
    img.onerror = () => {
      reject(new Error('Failed to load image for enhancement'));
    };
    
    img.src = dataUrl;
  });
};

/**
 * Process a File object and return compressed base64
 * @param {File} file - Image file to process
 * @param {function} onProgress - Progress callback (0-100)
 * @returns {Promise<string>} - Compressed base64 data URL
 */
export const processImageFile = (file, onProgress = () => {}) => {
  return new Promise((resolve, reject) => {
    onProgress(10);
    
    const reader = new FileReader();
    
    reader.onload = async (e) => {
      try {
        onProgress(30);
        const dataUrl = e.target.result;
        
        onProgress(50);
        // Use enhanced processing for better recognition
        const enhanced = await enhanceForRecognition(dataUrl);
        
        onProgress(80);
        // Additional compression if needed
        const compressed = await compressImage(enhanced);
        
        onProgress(100);
        resolve(compressed);
      } catch (error) {
        reject(error);
      }
    };
    
    reader.onerror = () => {
      reject(new Error('Failed to read image file'));
    };
    
    reader.readAsDataURL(file);
  });
};

/**
 * Estimate memory usage of base64 image
 * @param {string} base64 - Base64 string
 * @returns {number} - Estimated size in MB
 */
export const estimateImageSize = (base64) => {
  const base64Length = base64.length - (base64.indexOf(',') + 1);
  const sizeInBytes = (base64Length * 3) / 4;
  return sizeInBytes / (1024 * 1024);
};

/**
 * Check if image needs compression based on size
 * @param {string} dataUrl - Base64 data URL
 * @returns {boolean}
 */
export const needsCompression = (dataUrl) => {
  return estimateImageSize(dataUrl) > IMAGE_CONFIG.maxFileSizeMB;
};

/**
 * Capture image from video element with optimization
 * @param {HTMLVideoElement} video - Video element
 * @returns {Promise<string>} - Base64 data URL
 */
export const captureFromVideo = (video) => {
  return new Promise((resolve, reject) => {
    try {
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0);
      
      // Convert to JPEG with compression
      const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
      
      // Clean up
      canvas.width = 0;
      canvas.height = 0;
      
      resolve(dataUrl);
    } catch (error) {
      reject(error);
    }
  });
};

/**
 * Get optimized camera constraints for the device
 * @returns {object} - MediaStream constraints
 */
export const getCameraConstraints = () => {
  const isLowMemory = isLowMemoryDevice();
  
  return {
    video: {
      facingMode: 'environment', // Back camera
      width: { ideal: isLowMemory ? 640 : 1280 },
      height: { ideal: isLowMemory ? 480 : 720 },
      frameRate: { ideal: 30, max: 30 }
    }
  };
};

/**
 * Format file size for display
 * @param {number} bytes - Size in bytes
 * @returns {string} - Formatted size string
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Get size of base64 string in bytes
 * @param {string} base64 - Base64 string
 * @returns {number} - Size in bytes
 */
export const getBase64Size = (base64) => {
  const base64Length = base64.length - (base64.indexOf(',') + 1);
  return (base64Length * 3) / 4;
};

/**
 * Check if device has low memory
 * @returns {boolean}
 */
export const isLowMemoryDevice = () => {
  // Check for device memory API
  if ('deviceMemory' in navigator) {
    return navigator.deviceMemory <= 2; // 2GB or less
  }
  
  // Fallback: check for mobile user agent
  const userAgent = navigator.userAgent.toLowerCase();
  const isMobile = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent);
  
  return isMobile;
};

export default {
  compressImage,
  enhanceForRecognition,
  processImageFile,
  estimateImageSize,
  needsCompression,
  captureFromVideo,
  getCameraConstraints,
  formatFileSize,
  getBase64Size,
  isLowMemoryDevice,
  IMAGE_CONFIG
};
